import { useEffect, useState } from 'react'
import { Dialog } from '../Dialog'
import { WIKI } from '../../lib/hubCopy'
import { useHubLang } from '../../lib/i18n'
import { messageOf } from '../../lib/errors'
import { editWikiAnswer, loadWikiOriginal } from '../../lib/wiki'

/**
 * A curator changes the words of a Wiki One answer (187, WIK-28).
 *
 * ── IT EDITS THE SOURCE, NEVER WHAT IS ON SCREEN ─────────────────────────────
 * The row may be showing One's Spanish (112). Pre-filling from `r.question`
 * would let a Spanish-reading curator save a machine translation over the
 * English original, and the next translation would then be a translation of a
 * translation. So the fields are filled from wiki_original() — the source
 * language, always — and the dialog waits for it rather than guessing.
 *
 * ── SAVE IS OFF UNTIL SOMETHING CHANGED ──────────────────────────────────────
 * The database refuses a no-op (it would still read "Updated"); the button
 * says so first, so the refusal is never the way a person finds out.
 *
 * Typed text keeps the dialog open on a scrim press (Dialog's own rule, 29 Sep);
 * Escape and Cancel always close.
 */
export function EditAnswerDialog({
  contentId,
  open,
  onClose,
  onSaved,
}: {
  contentId: string
  open: boolean
  onClose: () => void
  /** Called after a successful save; the caller re-reads the list. */
  onSaved: () => void
}) {
  const w = WIKI[useHubLang()]
  const [orig, setOrig] = useState<{ question: string; answer: string; locale: string } | null>(null)
  const [question, setQuestion] = useState('')
  const [answer, setAnswer] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    let live = true
    setOrig(null)
    setError(null)
    loadWikiOriginal(contentId)
      .then((o) => {
        if (!live) return
        if (!o) {
          setError(w.editLoadFailed)
          return
        }
        setOrig({ question: o.title, answer: o.body, locale: o.locale })
        setQuestion(o.title)
        setAnswer(o.body)
      })
      .catch((e) => {
        if (live) setError(messageOf(e))
      })
    return () => {
      live = false
    }
    // w is copy, not state: re-running on a language switch would throw away
    // what the curator typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contentId])

  const unchanged =
    orig !== null && question.trim() === orig.question.trim() && answer.trim() === orig.answer.trim()
  const blank = question.trim() === '' || answer.trim() === ''

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!orig || unchanged || blank) return
    setSaving(true)
    setError(null)
    try {
      await editWikiAnswer(contentId, question, answer)
      onSaved()
      onClose()
    } catch (err) {
      setError(messageOf(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} title={w.editHeading} onClose={onClose} className="wikieditpanel">
      {orig === null && !error ? (
        <p className="editcard__hint">{w.editLoading}</p>
      ) : (
        <form className="editcard" onSubmit={(e) => void onSubmit(e)} noValidate>
          <p className="editcard__hint">{w.editNote}</p>
          <label className="profile__field editcard__field">
            <span className="profile__fieldlabel">{w.editQuestion}</span>
            <input
              className="profile__input"
              lang={orig?.locale}
              maxLength={300}
              value={question}
              disabled={orig === null}
              onChange={(e) => setQuestion(e.target.value)}
            />
          </label>
          <label className="profile__field editcard__field">
            <span className="profile__fieldlabel">{w.editAnswer}</span>
            <textarea
              className="profile__input wikiedit__answer"
              lang={orig?.locale}
              maxLength={5000}
              value={answer}
              disabled={orig === null}
              onChange={(e) => setAnswer(e.target.value)}
            />
          </label>

          {error && <p className="profile__error" role="alert">{error}</p>}

          <div className="editcard__btns">
            <button
              type="submit"
              className="btn btn--primary"
              disabled={saving || orig === null || unchanged || blank}
              title={unchanged ? w.editUnchanged : undefined}
            >
              {saving ? w.editSaving : w.editSave}
            </button>
            <button type="button" className="btn btn--quiet" disabled={saving} onClick={onClose}>
              {w.editCancel}
            </button>
          </div>
        </form>
      )}
    </Dialog>
  )
}
